import React, { useEffect, useState } from "react";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Modal,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";

import { ButtonVariant } from "Constant/button";
import { useDispatch, useSelector } from "react-redux";

import { trackerNotesEdit } from "helpers/trackers/trackerNotesEdit";

import StandartButton from "components/atoms/StandartButton";

import { getSingleTrackerRedux } from "../../redux/slices/trackers/trackers.asyncActions";
import SwalAlertComp from "../SwalAlert";

const TrackerNotes = ({ trackerId }) => {
    const dispatch = useDispatch();
    const language = useSelector((state) => state.languagesState.languages);
    const tracker = useSelector(
        (state) => state.allTrackersState.singleTracker,
    );

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [notesValue, setNotesValue] = useState("");
    const [originalNotes, setOriginalNotes] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const MAX_CHARACTERS = 2048;

    useEffect(() => {
        if (tracker?.tracker?.notes) {
            setNotesValue(tracker.tracker.notes || "");
            setOriginalNotes(tracker.tracker.notes || "");
        }
    }, [tracker]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
        setIsEditing(false);
        setNotesValue(originalNotes || "");
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setIsEditing(false);
        setNotesValue(originalNotes || "");
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleSaveClick = async () => {
        setIsSaving(true);

        // Trim the input and handle empty notes by sending single space instead of null
        const trimmedNotes = notesValue.trim();
        const notesToSend =
            trimmedNotes === " " || trimmedNotes === "" ? null : trimmedNotes;

        const response = await trackerNotesEdit(trackerId, {
            name: tracker.tracker.tracker_name,
            notes: notesToSend,
        });

        if (response.status) {
            setOriginalNotes(notesToSend || "");
            setNotesValue(notesToSend || "");
            setIsEditing(false);
            setIsSaving(false);
            dispatch(getSingleTrackerRedux(trackerId));
            SwalAlertComp({
                title: language?.tracker_detail?.success ?? "Success",
                message:
                    language?.tracker_detail?.notes_updated ??
                    "Notes updated successfully",
                icon: "success",
            });
        } else {
            SwalAlertComp({
                title: language?.tracker_detail?.failed ?? "Failed",
                message:
                    response.message ||
                    (language?.tracker_detail?.update_failed ??
                        "Failed to update notes"),
                icon: "error",
            });
            setIsSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setNotesValue(originalNotes || "");
    };

    const handleNotesChange = (e) => {
        const value = e.target.value;
        if (value.length <= MAX_CHARACTERS) {
            setNotesValue(value || "");
        }
    };

    const getCharacterCountColor = () => {
        const remaining = MAX_CHARACTERS - (notesValue?.length || 0);
        if (remaining < 100) return "error";
        if (remaining < 300) return "primary";
        return "textSecondary";
    };

    const hasNotes =
        originalNotes &&
        originalNotes.trim() !== " " &&
        originalNotes.trim() !== "" &&
        originalNotes.trim() !== " " &&
        originalNotes.trim() !== " ";

    return (
        <>
            <Tooltip title="Tracker Notes">
                <Box>
                    <StandartButton
                        onClick={handleOpenModal}
                        color="primary"
                        variant={ButtonVariant.BLACK_OUTLINED}
                        style={{
                            height: "100%",
                        }}
                    >
                        <Stack direction="row" gap={1} alignItems="center">
                            <NotesOutlinedIcon
                                sx={{ fontSize: { xs: "16px", sm: "20px" } }}
                            />
                            <Box>
                                {language?.tracker_detail?.notes ?? "Notes"}
                            </Box>
                            {hasNotes && (
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: hasNotes
                                            ? "#4caf50"
                                            : "#ff0000",
                                    }}
                                />
                            )}
                        </Stack>
                    </StandartButton>
                </Box>
            </Tooltip>

            <Modal
                open={isModalOpen}
                onClose={handleCloseModal}
                aria-labelledby="notes-modal-title"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        backgroundColor: "white",
                        borderRadius: 2,
                        padding: { xs: 2, sm: 3 },
                        maxWidth: 600,
                        maxHeight: "80vh",
                        overflow: "auto",
                        outline: "none",
                        margin: { xs: 1, sm: 0 },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 2,
                        }}
                    >
                        <Typography variant="h6" id="notes-modal-title">
                            {language?.tracker_detail?.tracker_notes ??
                                "Tracker Notes"}
                        </Typography>
                        <IconButton onClick={handleCloseModal}>
                            <CloseOutlinedIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ width: "100%", minWidth: "500px" }}>
                        {!isEditing ? (
                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 2,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ flex: 1, marginRight: 2 }}
                                    >
                                        {language?.tracker_detail
                                            ?.notes_description ??
                                            "Add notes, mapping details, and team information for this tracker"}
                                    </Typography>
                                    <Button
                                        onClick={handleEditClick}
                                        startIcon={<EditOutlinedIcon />}
                                        variant="outlined"
                                        size="small"
                                        color="primary"
                                        sx={{ flexShrink: 0 }}
                                    >
                                        {language?.tracker_detail?.edit ??
                                            "Edit"}
                                    </Button>
                                </Box>

                                <Box
                                    sx={{
                                        minHeight: 200,
                                        padding: 2,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                        backgroundColor: "#fafafa",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {hasNotes ? (
                                        <Typography variant="body1">
                                            {originalNotes || ""}
                                        </Typography>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{ fontStyle: "italic" }}
                                        >
                                            {language?.tracker_detail
                                                ?.no_notes ??
                                                "No notes added yet"}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ) : (
                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 2,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ marginBottom: 2 }}
                                    >
                                        {language?.tracker_detail
                                            ?.notes_description ??
                                            "Add notes, mapping details, and team information for this tracker"}
                                    </Typography>
                                    <Button
                                        onClick={handleEditClick}
                                        startIcon={<EditOutlinedIcon />}
                                        variant="outlined"
                                        size="small"
                                        color="primary"
                                        sx={{ flexShrink: 0, opacity: 0 }}
                                    >
                                        {language?.tracker_detail?.edit ??
                                            "Edit"}
                                    </Button>
                                </Box>
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        marginBottom: 2,
                                    }}
                                >
                                    <textarea
                                        value={notesValue}
                                        onChange={handleNotesChange}
                                        placeholder={
                                            language?.tracker_detail
                                                ?.notes_placeholder ??
                                            "Enter your notes here...\n\nExample:\n- Mapping details\n- Team coordination notes\n- Event explanations"
                                        }
                                        disabled={isSaving}
                                        style={{
                                            width: "100%",
                                            minHeight:
                                                window.innerWidth < 600
                                                    ? "150px"
                                                    : "200px",
                                            padding:
                                                window.innerWidth < 600
                                                    ? "10px"
                                                    : "14px",
                                            border: "1px solid #c4c4c4",
                                            borderRadius: "4px",
                                            fontSize:
                                                window.innerWidth < 600
                                                    ? "12px"
                                                    : "14px",
                                            fontFamily: "inherit",
                                            resize: "none",
                                            outline: "none",
                                            boxSizing: "border-box",
                                            backgroundColor: isSaving
                                                ? "#f5f5f5"
                                                : "white",
                                            cursor: isSaving
                                                ? "not-allowed"
                                                : "text",
                                        }}
                                        onFocus={(e) => {
                                            if (!isSaving) {
                                                e.target.style.borderColor =
                                                    "#1976d2";
                                            }
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor =
                                                "#c4c4c4";
                                        }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 2,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color={getCharacterCountColor()}
                                    >
                                        {notesValue.length || 0} /{" "}
                                        {MAX_CHARACTERS}{" "}
                                        {language?.tracker_detail?.characters ??
                                            "characters"}
                                    </Typography>
                                    {(notesValue.length || 0) >
                                        MAX_CHARACTERS - 100 && (
                                        <Typography
                                            variant="caption"
                                            color="warning.main"
                                        >
                                            {language?.tracker_detail
                                                ?.character_warning ??
                                                "Approaching character limit"}
                                        </Typography>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1,
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Button
                                        onClick={handleCancelEdit}
                                        variant="outlined"
                                        size="small"
                                        disabled={isSaving}
                                        color="primary"
                                        style={{
                                            minWidth: "unset !important",
                                        }}
                                    >
                                        {language?.tracker_detail?.cancel ??
                                            "Cancel"}
                                    </Button>
                                    <Button
                                        onClick={handleSaveClick}
                                        startIcon={
                                            isSaving && (
                                                <CircularProgress
                                                    size={16}
                                                    color="inherit"
                                                />
                                            )
                                        }
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        disabled={isSaving}
                                        style={{
                                            minWidth: "130px !important",
                                        }}
                                    >
                                        {isSaving
                                            ? (language?.tracker_detail
                                                  ?.saving ?? "Saving...")
                                            : (language?.tracker_detail?.save ??
                                              "Save")}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default TrackerNotes;
